import { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, Circle, useMap, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Create custom icons for start/end
const createIcon = (color, label) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="
    background: ${color};
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 10px;
  ">${label}</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const startIcon = createIcon('#22c55e', 'S');
const endIcon = createIcon('#ef4444', 'E');

// Component to fit map bounds to the route
function FitBounds({ points }) {
  const map = useMap();
  
  useEffect(() => {
    if (points && points.length > 0) {
      const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, points]);
  
  return null;
}

// Color scale for heatmap intensity (gradient from blue to red)
const getHeatmapColor = (intensity) => {
  // Create gradient: blue -> cyan -> green -> yellow -> orange -> red
  if (intensity >= 90) return '#ff0000';      // Very high - Red
  if (intensity >= 75) return '#ff4400';      // High - Red-Orange
  if (intensity >= 60) return '#ff8800';      // Medium-high - Orange
  if (intensity >= 45) return '#ffcc00';      // Medium - Yellow
  if (intensity >= 30) return '#88ff00';      // Low-medium - Yellow-Green
  if (intensity >= 15) return '#00ff88';      // Low - Green-Cyan
  return '#00ccff';                            // Very low - Cyan
};

// Get speed-based color for route segments
const getSpeedColor = (speed) => {
  const kmh = speed * 3.6; // Convert m/s to km/h
  if (kmh >= 25) return '#ff0000';   // Sprint (>25 km/h)
  if (kmh >= 20) return '#ff6600';   // High speed (20-25 km/h)
  if (kmh >= 15) return '#ffcc00';   // Medium-high (15-20 km/h)
  if (kmh >= 10) return '#66ff00';   // Medium (10-15 km/h)
  if (kmh >= 5) return '#00ccff';    // Jog (5-10 km/h)
  return '#0066ff';                   // Walk (<5 km/h)
};

export default function GPSMapVisualization({ 
  points = [], 
  heatmap = [], 
  viewMode = 'route',  // 'route', 'heatmap', 'both'
  showSpeedColors = true,
  showStartEnd = true
}) {
  // Calculate center from points
  const getCenter = () => {
    if (points.length === 0 && heatmap.length === 0) {
      return [51.5074, -0.1278]; // Default to London
    }
    
    const allPoints = points.length > 0 
      ? points 
      : heatmap.map(h => ({ latitude: h.lat, longitude: h.lng }));
    
    const avgLat = allPoints.reduce((sum, p) => sum + (p.latitude || p.lat), 0) / allPoints.length;
    const avgLng = allPoints.reduce((sum, p) => sum + (p.longitude || p.lng), 0) / allPoints.length;
    
    return [avgLat, avgLng];
  };

  // Create route line with speed-based colors
  const createRouteSegments = useMemo(() => {
    if (!showSpeedColors || points.length < 2) {
      return [{ 
        positions: points.map(p => [p.latitude, p.longitude]), 
        color: '#02b5ff' 
      }];
    }

    const segments = [];
    for (let i = 1; i < points.length; i++) {
      const speed = points[i].speed || 0;
      segments.push({
        positions: [
          [points[i-1].latitude, points[i-1].longitude],
          [points[i].latitude, points[i].longitude]
        ],
        color: getSpeedColor(speed)
      });
    }
    return segments;
  }, [points, showSpeedColors]);

  // Generate heatmap overlay from points if not provided
  const heatmapData = useMemo(() => {
    if (heatmap.length > 0) return heatmap;
    if (points.length < 10) return [];
    
    // Create grid-based heatmap from points
    const gridSize = 0.0001; // ~10 meters
    const grid = {};
    
    points.forEach(point => {
      const gridX = Math.floor(point.latitude / gridSize);
      const gridY = Math.floor(point.longitude / gridSize);
      const key = `${gridX},${gridY}`;
      
      if (!grid[key]) {
        grid[key] = {
          lat: (gridX + 0.5) * gridSize,
          lng: (gridY + 0.5) * gridSize,
          count: 0,
          totalSpeed: 0
        };
      }
      grid[key].count++;
      grid[key].totalSpeed += point.speed || 0;
    });
    
    // Convert to array and calculate intensity
    const maxCount = Math.max(...Object.values(grid).map(g => g.count));
    return Object.values(grid).map(cell => ({
      lat: cell.lat,
      lng: cell.lng,
      intensity: (cell.count / maxCount) * 100,
      avgSpeed: cell.totalSpeed / cell.count
    }));
  }, [points, heatmap]);

  if ((points.length === 0 && heatmap.length === 0)) {
    return (
      <div className="h-[400px] bg-zinc-900 rounded-lg flex items-center justify-center">
        <p className="text-zinc-500">No GPS data to display</p>
      </div>
    );
  }

  const center = getCenter();
  const startPoint = points.length > 0 ? points[0] : null;
  const endPoint = points.length > 1 ? points[points.length - 1] : null;

  return (
    <div className="relative">
      <MapContainer
        center={center}
        zoom={15}
        style={{ height: '400px', width: '100%', borderRadius: '8px' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Fit bounds to points */}
        {points.length > 0 && <FitBounds points={points} />}
        
        {/* Heatmap circles (render first so routes overlay on top) */}
        {(viewMode === 'heatmap' || viewMode === 'both') && heatmapData.length > 0 && (
          <>
            {heatmapData.map((cell, idx) => (
              <Circle
                key={`heat-${idx}`}
                center={[cell.lat, cell.lng]}
                radius={8 + (cell.intensity / 8)}
                pathOptions={{
                  fillColor: getHeatmapColor(cell.intensity),
                  fillOpacity: viewMode === 'heatmap' ? 0.7 : 0.4,
                  color: getHeatmapColor(cell.intensity),
                  weight: 0
                }}
              />
            ))}
          </>
        )}
        
        {/* Route lines */}
        {(viewMode === 'route' || viewMode === 'both') && points.length > 0 && (
          <>
            {createRouteSegments.map((segment, idx) => (
              <Polyline
                key={`route-${idx}`}
                positions={segment.positions}
                pathOptions={{ 
                  color: segment.color, 
                  weight: 4,
                  opacity: 0.9 
                }}
              />
            ))}
          </>
        )}
        
        {/* Start marker */}
        {showStartEnd && startPoint && (
          <Marker 
            position={[startPoint.latitude, startPoint.longitude]}
            icon={startIcon}
          >
            <Popup>
              <div className="text-center">
                <strong>Start</strong>
                {startPoint.timestamp && (
                  <div className="text-xs text-gray-600">
                    {new Date(startPoint.timestamp).toLocaleTimeString()}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* End marker */}
        {showStartEnd && endPoint && (
          <Marker 
            position={[endPoint.latitude, endPoint.longitude]}
            icon={endIcon}
          >
            <Popup>
              <div className="text-center">
                <strong>End</strong>
                {endPoint.timestamp && (
                  <div className="text-xs text-gray-600">
                    {new Date(endPoint.timestamp).toLocaleTimeString()}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
      
      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-zinc-900/90 backdrop-blur-sm border border-zinc-700 rounded-lg p-3 z-[1000]">
        <div className="text-xs font-bold text-zinc-400 uppercase mb-2">Speed Legend</div>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-2 rounded" style={{ backgroundColor: '#ff0000' }} />
            <span className="text-zinc-300">Sprint (&gt;25 km/h)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-2 rounded" style={{ backgroundColor: '#ff6600' }} />
            <span className="text-zinc-300">High (20-25 km/h)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-2 rounded" style={{ backgroundColor: '#ffcc00' }} />
            <span className="text-zinc-300">Medium (15-20 km/h)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-2 rounded" style={{ backgroundColor: '#66ff00' }} />
            <span className="text-zinc-300">Jog (10-15 km/h)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-2 rounded" style={{ backgroundColor: '#00ccff' }} />
            <span className="text-zinc-300">Light (5-10 km/h)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-2 rounded" style={{ backgroundColor: '#0066ff' }} />
            <span className="text-zinc-300">Walk (&lt;5 km/h)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
